import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import ThreeBackground from "../common/ThreeBackground";
import Icon from "../common/Icon";
import { brand, whatWeDo } from "../../Data/siteContent";

const ease = [0.21, 0.8, 0.25, 1];

export default function HeroSection() {
  const [line, setLine] = useState(0);

  useEffect(() => {
    const id = setInterval(
      () => setLine((l) => (l + 1) % brand.heroHeadlines.length),
      4000
    );
    return () => clearInterval(id);
  }, []);

  return (
    <header className="cv-hero" id="top">
      <div className="cv-grid-bg" />
      <ThreeBackground variant="core" className="cv-hero__particles" />
      <div className="cv-hero__glow cv-hero__glow--cyan" aria-hidden="true" />
      <div className="cv-hero__glow cv-hero__glow--coffee" aria-hidden="true" />

      <div className="cv-hero__inner">
        {/* Left — message */}
        <div className="cv-hero__copy">
          <motion.span
            className="cv-eyebrow"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease }}
          >
            <span className="dot" />
            Business Solutions · Cafevista Pvt Ltd
          </motion.span>

          <motion.h1
            className="cv-hero__title"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.08, ease }}
          >
            Business challenges in.{" "}
            <span className="cv-gradient-text">Working solutions out</span>.
          </motion.h1>

          <div className="cv-hero__rotor" aria-live="polite">
            <AnimatePresence mode="wait">
              <motion.span
                key={line}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.5, ease }}
              >
                {brand.heroHeadlines[line]}
              </motion.span>
            </AnimatePresence>
          </div>

          <motion.p
            className="cv-hero__sub"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.18, ease }}
          >
            We study how your business actually runs, engineer the system that fixes the
            bottleneck, and integrate it with the ERP, data and tools you already own.
            Technology is how we solve it &mdash; the business result is the point.
          </motion.p>

          <motion.div
            className="cv-hero__cta"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.26, ease }}
          >
            <Link to="/contactus" className="cv-btn">
              Bring us your challenge
              <Icon name="arrow" size={18} className="arrow" />
            </Link>
            <a href="#story" className="cv-btn cv-btn--ghost">
              <Icon name="signal" size={18} />
              See how we work
            </a>
          </motion.div>

          <motion.ul
            className="cv-hero__chips"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            {whatWeDo.capabilities.slice(0, 5).map((c) => (
              <li key={c} className="cv-chip">{c}</li>
            ))}
          </motion.ul>
        </div>

        {/* Right — scattered systems resolving into one connected view */}
        <motion.div
          className="cv-hero__stage"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.2, ease }}
        >
          <ConnectedView />
        </motion.div>
      </div>

      <a href="#story" className="cv-hero__scroll" aria-label="Scroll down">
        <span className="cv-hero__mouse"><span /></span>
        The process, start to finish
      </a>
    </header>
  );
}

/* The pitch in one image: disconnected sources feeding one live view. */
const SOURCES = [
  { label: "ERP", top: "6%", left: "-6%", d: 0 },
  { label: "Finance", top: "34%", left: "-13%", d: 0.6 },
  { label: "Warehouse", top: "64%", left: "-5%", d: 1.2 },
];

function ConnectedView() {
  const bars = [42, 68, 55, 88, 73, 95, 61];
  return (
    <div className="holo">
      <div className="holo__ring holo__ring--1" />
      <div className="holo__ring holo__ring--2" />

      {SOURCES.map((s) => (
        <motion.span
          key={s.label}
          className="holo__source cv-glass"
          style={{ top: s.top, left: s.left }}
          animate={{ y: [0, -8, 0] }}
          transition={{ duration: 6 + s.d, repeat: Infinity, ease: "easeInOut", delay: s.d }}
        >
          <i />
          {s.label}
        </motion.span>
      ))}

      <motion.div
        className="holo__panel cv-glass"
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="holo__bar-top">
          <span className="holo__dots"><i /><i /><i /></span>
          <span className="holo__tag">one connected view</span>
          <span className="holo__live"><i /> live</span>
        </div>

        <div className="holo__metrics">
          <div className="holo__metric">
            <span>On-time orders</span>
            <strong className="cv-cyan-text">96.2%</strong>
          </div>
          <div className="holo__metric">
            <span>Cycle time</span>
            <strong className="cv-coffee-text">-41%</strong>
          </div>
          <div className="holo__metric">
            <span>Manual steps</span>
            <strong>12 &rarr; 2</strong>
          </div>
        </div>

        <div className="holo__chart">
          {bars.map((h, i) => (
            <motion.span
              key={i}
              className="holo__col"
              initial={{ height: 6 }}
              whileInView={{ height: `${h}%` }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.3 + i * 0.08, ease }}
            />
          ))}
        </div>
        <div className="holo__axis">
          <span>orders</span>
          <span>production</span>
          <span>stock</span>
          <span>margin</span>
        </div>
      </motion.div>

      <motion.div
        className="holo__chip holo__chip--chat cv-glass"
        animate={{ y: [0, -14, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        <span className="holo__chip-ic holo__chip-ic--cyan"><Icon name="chat" size={18} /></span>
        <div>
          <strong>Ask your data</strong>
          <span>&ldquo;Which orders are late?&rdquo;</span>
        </div>
      </motion.div>

      <motion.div
        className="holo__chip holo__chip--auto cv-glass"
        animate={{ y: [0, 12, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        <span className="holo__chip-ic holo__chip-ic--coffee"><Icon name="gears" size={18} /></span>
        <div>
          <strong>Approval routed</strong>
          <span>PO #1042 &middot; automatic</span>
        </div>
      </motion.div>
    </div>
  );
}
